import React, { useState, useMemo, useEffect } from "react";
import { Invoice } from "../../../../features/groups/groupSlice";
import InvoiceCard from "./InvoiceCard";
import InvoiceFilters from "./InvoiceFilters";
import InvoicePagination from "./InvoicePagination";
import { MdDownload, MdFilterList } from "react-icons/md";
import DownloadFormatModal from "./DownloadFormatModal";
import { DownloadOptions, downloadMultipleInvoices } from "../utils";
import { toast } from "react-toastify";
import useGetLanguage from "../../../../utils/useGetLanguage";
import { LanguageText } from "../../../../dictionary/GroupsText";
interface InvoiceTableProps {
  invoices: Invoice[];
}

const InvoiceTable: React.FC<InvoiceTableProps> = ({ invoices }) => {
  const language: LanguageText = useGetLanguage({
    fileName: "GroupsText",
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [dateFilter, setDateFilter] = useState<string>("all");
  const [sortBy, setSortBy] = useState<keyof Invoice>("invoice_date");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, dateFilter]);

  // Filter and sort invoices
  const filteredAndSortedInvoices = useMemo(() => {
    let filtered = invoices.filter((invoice) => {
      // Enhanced search - search in multiple fields
      const searchLower = searchTerm.toLowerCase();

      // Format currency for search (e.g., "£10.21" or "$10.21")
      const formattedAmount = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: invoice.invoice_currency,
        minimumFractionDigits: 2,
      }).format(invoice.invoice_amount / 100);

      const matchesSearch =
        searchTerm === "" ||
        invoice.invoice_number.toLowerCase().includes(searchLower) ||
        invoice.invoice_id.toLowerCase().includes(searchLower) ||
        invoice.invoice_status.toLowerCase().includes(searchLower) ||
        invoice.invoice_currency.toLowerCase().includes(searchLower) ||
        invoice.stripe_payment_intent_id.toLowerCase().includes(searchLower) ||
        formattedAmount.toLowerCase().includes(searchLower) ||
        // Also search for raw amount
        invoice.invoice_amount.toString().includes(searchTerm) ||
        // Search for currency symbols
        (searchLower.includes("£") && invoice.invoice_currency === "GBP") ||
        (searchLower.includes("$") &&
          (invoice.invoice_currency === "USD" || invoice.invoice_currency === "CAD")) ||
        (searchLower.includes("€") && invoice.invoice_currency === "EUR");

      const matchesStatus =
        statusFilter === "all" || invoice.invoice_status === statusFilter;

      const matchesDate = (() => {
        if (dateFilter === "all") return true;

        const now = new Date();
        const invoiceDate = new Date(invoice.invoice_date);

        switch (dateFilter) {
          case "last-30-days":
            return invoiceDate >= new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          case "last-90-days":
            return invoiceDate >= new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
          case "last-year":
            return invoiceDate.getFullYear() === now.getFullYear() - 1;
          case "this-year":
            return invoiceDate.getFullYear() === now.getFullYear();
          default:
            return true;
        }
      })();

      return matchesSearch && matchesStatus && matchesDate;
    });

    // Sort invoices
    filtered.sort((a, b) => {
      const aValue = a[sortBy];
      const bValue = b[sortBy];

      if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
      if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [invoices, searchTerm, statusFilter, dateFilter, sortBy, sortOrder]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedInvoices.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedInvoices = filteredAndSortedInvoices.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  const handleSort = (field: keyof Invoice) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  const handleDownloadAll = () => {
    setIsDownloadModalOpen(true);
  };

  const handleBulkDownloadConfirm = (options: DownloadOptions) => {
    try {
      // Use all invoices for bulk download (not filtered)
      downloadMultipleInvoices(invoices, options);
      toast.success(`${invoices.length} invoices downloaded successfully!`);
    } catch (error) {
      console.error("Bulk download error:", error);
      toast.error("Failed to download invoices. Please try again.");
    }
  };

  const getStatusCounts = () => {
    const counts = invoices.reduce((acc, invoice) => {
      acc[invoice.invoice_status] = (acc[invoice.invoice_status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    return counts;
  };

  const statusCounts = getStatusCounts();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            {language?.groupSubscription?.invoices}
          </h2>
          <p className="text-gray-600">
            {`${filteredAndSortedInvoices.length} ${language?.groupSubscription?.of}
            ${invoices.length} ${language?.groupSubscription?.invoices}`}
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <button
            onClick={handleDownloadAll}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <MdDownload className="w-4 h-4 mr-2" />
            {language?.groupSubscription?.downloadAll}
          </button>
        </div>
      </div>

      {/* Filters */}
      <InvoiceFilters
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        dateFilter={dateFilter}
        onDateFilterChange={setDateFilter}
        statusCounts={statusCounts}
      />

      {/* Desktop Table View */}
      <div className="hidden lg:block">
        <div className="bg-white shadow-sm rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    onClick={() => handleSort("invoice_number")}
                    className="w-1/5 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    <div className="flex items-center space-x-1">
                      <span>{language?.groupSubscription?.invoice} #</span>
                      {sortBy === "invoice_number" && (
                        <span className="text-blue-600">
                          {sortOrder === "asc" ? "↑" : "↓"}
                        </span>
                      )}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort("invoice_date")}
                    className="w-1/5 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    <div className="flex items-center space-x-1">
                      <span>{language?.groupSubscription?.date}</span>
                      {sortBy === "invoice_date" && (
                        <span className="text-blue-600">
                          {sortOrder === "asc" ? "↑" : "↓"}
                        </span>
                      )}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort("invoice_amount")}
                    className="w-1/5 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    <div className="flex items-center space-x-1">
                      <span>{language?.groupSubscription?.amount}</span>
                      {sortBy === "invoice_amount" && (
                        <span className="text-blue-600">
                          {sortOrder === "asc" ? "↑" : "↓"}
                        </span>
                      )}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort("invoice_status")}
                    className="w-1/5 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    <div className="flex items-center space-x-1">
                      <span>{language?.groupSubscription?.status}</span>
                      {sortBy === "invoice_status" && (
                        <span className="text-blue-600">
                          {sortOrder === "asc" ? "↑" : "↓"}
                        </span>
                      )}
                    </div>
                  </th>
                  <th className="w-1/5 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {language?.groupSubscription?.actions}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedInvoices.map((invoice) => (
                  <InvoiceCard
                    key={invoice.invoice_id}
                    invoice={invoice}
                    variant="table-row"
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Mobile Card View */}
      <div className="lg:hidden space-y-4">
        {paginatedInvoices.map((invoice) => (
          <InvoiceCard key={invoice.invoice_id} invoice={invoice} variant="card" />
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <InvoicePagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      )}

      {/* Empty State - No invoices after filtering */}
      {filteredAndSortedInvoices.length === 0 && (
        <div className="text-center py-12">
          <div className="mx-auto h-24 w-24 text-gray-400">
            <MdFilterList className="h-full w-full" />
          </div>
          <h3 className="mt-4 text-lg font-medium text-gray-900">No invoices found</h3>
          <p className="mt-2 text-gray-500">
            Try adjusting your search or filter criteria.
          </p>
        </div>
      )}

      {/* Download Format Modal */}
      <DownloadFormatModal
        isOpen={isDownloadModalOpen}
        onClose={() => setIsDownloadModalOpen(false)}
        onConfirm={handleBulkDownloadConfirm}
        title={language?.groupSubscription?.downloadAllInvoices}
        subtitle={`${language?.groupSubscription?.download} ${invoices.length} ${language?.groupSubscription?.invoices}`}
        isBulk={true}
      />
    </div>
  );
};

export default InvoiceTable;
